import React, { useState } from "react";
import { BookOpen, Check, Copy, KeyRound, Webhook } from "lucide-react";
import { toast } from "sonner";

const API_ROOT = "https://api.clivercrm.com.br/api/public";

const CREATE_EXAMPLES = {
  curl: `curl -X POST "${API_ROOT}/leads" \\
  -H "Authorization: Bearer cliver_live_SUA_CHAVE" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Maria Silva","phone":"5516999999999","email":"maria@empresa.com"}'`,
  javascript: `const response = await fetch("${API_ROOT}/leads", {
  method: "POST",
  headers: {
    "Authorization": "Bearer cliver_live_SUA_CHAVE",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    name: "Maria Silva",
    phone: "5516999999999",
    email: "maria@empresa.com"
  })
});
const data = await response.json();`,
  python: `import requests

response = requests.post(
    "${API_ROOT}/leads",
    headers={"Authorization": "Bearer cliver_live_SUA_CHAVE"},
    json={
        "name": "Maria Silva",
        "phone": "5516999999999",
        "email": "maria@empresa.com"
    },
    timeout=10
)
print(response.json())`,
  php: `<?php
$ch = curl_init("${API_ROOT}/leads");
curl_setopt_array($ch, [
  CURLOPT_POST => true,
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_HTTPHEADER => [
    "Authorization: Bearer cliver_live_SUA_CHAVE",
    "Content-Type: application/json"
  ],
  CURLOPT_POSTFIELDS => json_encode([
    "name" => "Maria Silva",
    "phone" => "5516999999999"
  ])
]);
echo curl_exec($ch);`,
  n8n: `Metodo: POST
URL: ${API_ROOT}/leads
Authentication: Header Auth
Header: Authorization
Value: Bearer cliver_live_SUA_CHAVE
Body Content Type: JSON
Body: { "name": "{{$json.name}}", "phone": "{{$json.phone}}" }`,
};

const ENDPOINTS = [
  { method: "GET", path: "/leads/{id}", title: "Buscar Lead", body: null },
  { method: "PATCH", path: "/leads/{id}", title: "Atualizar Lead", body: '{ "name": "Novo nome", "value": 1500 }' },
  { method: "POST", path: "/leads/{id}/move", title: "Mover Lead", body: '{ "column_id": "ID_DA_ETAPA", "pipeline_id": "ID_DO_FUNIL" }' },
  { method: "POST", path: "/tasks", title: "Criar Tarefa", body: '{ "lead_id": "ID_DO_LEAD", "title": "Retornar contato", "due_date": "2026-07-10T15:00:00Z", "type": "follow_up" }' },
  { method: "GET", path: "/pipelines", title: "Buscar Pipelines", body: null },
  { method: "GET", path: "/columns", title: "Buscar Colunas", body: null },
];

const WEBHOOK_EVENTS = [
  "lead.created", "lead.updated", "lead.moved", "deal.won", "deal.lost",
  "task.created", "task.completed", "message.received", "message.sent", "contact.created",
];

const ERROR_CODES = [
  ["401", "API Key ausente ou invalida"],
  ["403", "API Key sem permissao, desabilitada ou expirada"],
  ["404", "Recurso nao encontrado neste workspace"],
  ["422", "Dados enviados invalidos"],
  ["500", "Erro interno inesperado"],
];

function CodeBlock({ code, label = "Exemplo" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Exemplo copiado.");
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Nao foi possivel copiar o exemplo.");
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-950">
      <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2">
        <span className="text-[10px] font-semibold uppercase text-slate-500">{label}</span>
        <button type="button" onClick={copy} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-800 hover:text-slate-200" aria-label={`Copiar ${label}`} title="Copiar">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      <pre className="overflow-x-auto p-4 text-[11px] leading-5 text-cyan-200"><code>{code}</code></pre>
    </div>
  );
}

function Section({ id, title, description, children }) {
  return (
    <section id={id} className="scroll-mt-24 border-b border-slate-800/80 py-7 last:border-b-0">
      <h3 className="text-base font-semibold text-slate-100">{title}</h3>
      {description && <p className="mt-2 max-w-3xl text-xs leading-5 text-slate-500">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function ApiDocumentationPanel() {
  const [language, setLanguage] = useState("curl");

  return (
    <article className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900/60">
      <header className="border-b border-slate-800 px-5 py-5 sm:px-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cyan-400/10">
            <BookOpen className="h-5 w-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-100">Documentacao da API</h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">Conecte sistemas externos ao Cliver CRM usando REST API e webhooks assinados.</p>
          </div>
        </div>
      </header>

      <div className="grid lg:grid-cols-[190px_minmax(0,1fr)]">
        <nav className="hidden border-r border-slate-800 p-4 lg:block" aria-label="Sumario da documentacao">
          {["Autenticacao", "Criar Lead", "Endpoints", "Webhooks", "Erros"].map((item) => (
            <a key={item} href={`#api-${item.toLowerCase().replace(" ", "-")}`} className="block rounded-md px-3 py-2 text-xs text-slate-500 hover:bg-slate-800/70 hover:text-slate-200">{item}</a>
          ))}
        </nav>

        <div className="min-w-0 px-5 sm:px-7">
          <Section id="api-autenticacao" title="Autenticacao" description="Envie sua API Key em um dos headers aceitos. A chave pertence a um unico workspace e nunca deve ser exposta no navegador.">
            <div className="grid gap-3 md:grid-cols-2">
              <CodeBlock label="Bearer Token" code="Authorization: Bearer cliver_live_SUA_CHAVE" />
              <CodeBlock label="Header alternativo" code="X-Cliver-Api-Key: cliver_live_SUA_CHAVE" />
            </div>
          </Section>

          <Section id="api-criar-lead" title="Criar Lead" description={`POST ${API_ROOT}/leads`}>
            <div className="mb-3 flex flex-wrap gap-1" role="tablist" aria-label="Linguagem do exemplo">
              {Object.keys(CREATE_EXAMPLES).map((item) => (
                <button key={item} type="button" role="tab" aria-selected={language === item} onClick={() => setLanguage(item)} className={`rounded-md border px-3 py-1.5 text-[11px] font-medium ${language === item ? "border-cyan-400/30 bg-cyan-400/10 text-cyan-300" : "border-slate-800 text-slate-500 hover:text-slate-300"}`}>
                  {item === "javascript" ? "JavaScript" : item === "python" ? "Python" : item === "php" ? "PHP" : item === "n8n" ? "n8n" : "cURL"}
                </button>
              ))}
            </div>
            <CodeBlock label={language} code={CREATE_EXAMPLES[language]} />
          </Section>

          <Section id="api-endpoints" title="Endpoints" description="Todas as rotas abaixo exigem uma API Key valida e acessam somente o workspace associado a ela.">
            <div className="space-y-3">
              {ENDPOINTS.map((endpoint) => (
                <div key={`${endpoint.method}-${endpoint.path}`} className="rounded-lg border border-slate-800 bg-slate-950/35 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded px-2 py-1 text-[10px] font-bold ${endpoint.method === "GET" ? "bg-cyan-400/10 text-cyan-300" : endpoint.method === "PATCH" ? "bg-amber-400/10 text-amber-300" : "bg-emerald-400/10 text-emerald-300"}`}>{endpoint.method}</span>
                    <code className="break-all text-xs text-slate-200">{endpoint.path}</code>
                    <span className="text-xs text-slate-500">{endpoint.title}</span>
                  </div>
                  {endpoint.body && <div className="mt-3"><CodeBlock label="JSON" code={endpoint.body} /></div>}
                </div>
              ))}
            </div>
          </Section>

          <Section id="api-webhooks" title="Webhooks" description="Os eventos sao enviados em JSON. Use a assinatura para validar que a requisicao foi gerada pelo Cliver CRM.">
            <div className="grid gap-4 xl:grid-cols-2">
              <div className="rounded-lg border border-slate-800 bg-slate-950/35 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-200"><Webhook className="h-4 w-4 text-emerald-400" />Eventos disponiveis</div>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {WEBHOOK_EVENTS.map((event) => <code key={event} className="rounded border border-slate-800 bg-slate-950 px-2 py-1.5 text-[11px] text-slate-400">{event}</code>)}
                </div>
                <p className="mt-3 text-[11px] leading-5 text-slate-600"><code>contact.created</code> esta reservado para a futura entidade de contatos separada.</p>
              </div>
              <div className="space-y-3">
                <CodeBlock label="Assinatura HMAC SHA-256" code="X-Cliver-Signature: sha256=..." />
                <div className="rounded-lg border border-slate-800 bg-slate-950/35 p-4 text-xs leading-5 text-slate-500">
                  <div className="mb-2 flex items-center gap-2 font-semibold text-slate-200"><KeyRound className="h-4 w-4 text-cyan-400" />Headers enviados</div>
                  <code className="block">Content-Type: application/json</code>
                  <code className="block">X-Cliver-Event</code>
                  <code className="block">X-Cliver-Timestamp</code>
                  <code className="block">X-Cliver-Signature (quando houver secret)</code>
                </div>
              </div>
            </div>
          </Section>

          <Section id="api-erros" title="Codigos de erro" description="A API utiliza codigos HTTP padrao e mensagens curtas no campo detail.">
            <div className="overflow-hidden rounded-lg border border-slate-800">
              {ERROR_CODES.map(([code, description]) => (
                <div key={code} className="grid grid-cols-[70px_1fr] border-b border-slate-800 px-4 py-3 text-xs last:border-b-0">
                  <code className="font-semibold text-cyan-300">{code}</code>
                  <span className="text-slate-500">{description}</span>
                </div>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </article>
  );
}